import React, { useRef, useEffect } from "react";
import videojs from "video.js";
import "videojs-contrib-quality-levels";
import "videojs-hls-quality-selector";
import { useVideoJSPlayerAnalytics } from "../../features/analytics/video-analytics";
import { useVideoProgress } from "../../features/hooks/useVideoProgress";

const defaultPlaybackRates = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

const VideoPlayer = ({
  id,
  src,
  poster = "",
  playbackRates = defaultPlaybackRates,
  duration = 0
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const { registerVideoAnalyticsHandlers, resetVideoAnalytics } = useVideoJSPlayerAnalytics(id);
  const { handlePlayerReady, handleProgressOnSrcChange } = useVideoProgress(
    id,
    playerRef.current,
    duration
  );

  useEffect(() => {
    if (!playerRef.current) {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      const videoJsOptions = {
        autoplay: false,
        controls: true,

        playbackRates,
        poster,
        sources: [
          {
            src,
            type: "application/x-mpegURL"
          }
        ],

        controlBar: {
          children: [
            "playToggle",
            "volumePanel",
            "currentTimeDisplay", // Make sure this is included
            "progressControl",
            "playbackRateMenuButton",
            "fullscreenToggle"

            // Explicitly leave out 'subtitlesButton' and 'captionsButton'
          ]
        },
        plugins: {
          qualityLevels: {},
          hlsQualitySelector: {}
        }
      };

      const player = videojs(videoElement, videoJsOptions, handlePlayerReady);
      playerRef.current = player;

      player.error(null);
      player.src(src);
      player.preload(false);
      player.fluid(true);
      // player.errorDisplay(false);
      // player.liveui(true);
      player.aspectRatio("16:9");
      player.autoplay("any");
      player.playsinline(true);
      registerVideoAnalyticsHandlers(player);
    } else {
      // update player here [update player through props]
      const player = playerRef.current;
      player.src(src);
      resetVideoAnalytics();
      handleProgressOnSrcChange();
    }
    // intentionally not including userProgress in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, poster, playbackRates, registerVideoAnalyticsHandlers, resetVideoAnalytics]);

  return (
    <div data-vjs-player>
      <video
        ref={videoRef}
        webkit-playsinline="true"
        playsInline={true}
        className={`video-js vjs-tribe vjs-big-play-centered vjs-fluid`}
      />
    </div>
  );
};

export default VideoPlayer;
